import { API_BASE_URL, handleApiResponse } from "@/lib/api";
import { UserModelJsonResource } from "@/types/userTypes";
import { useAuthStore } from "@/store/auth";

export interface AuthResponse {
  user: UserModelJsonResource;
  token: string;
}

export interface GoogleAuthData {
  idToken: string;
  deviceTimezoneOffset: string;
  invitation_token?: string;
}

export interface AppleAuthData {
  idToken: string;
  deviceTimezoneOffset: string;
  device: "web" | "ios" | "android";
  invitation_token?: string;
  //authorizationCode: string; // 用于服务器端 OAuth2 token 交换流程, 如果你需要进一步调用 Apple 的 OAuth API（如 access_token / refresh_token）时才需要它, 类似于 Google OAuth2 中的 code → token 流程. 大多数网站登录场景完全用不到 code
  // user?: {
  //   name?: {
  //     firstName?: string;
  //     lastName?: string;
  //   };
  //   email?: string;
  // };
}

// API functions for authentication
export const authApi = {
  // Login with Google
  googleLogin: async (googleData: GoogleAuthData): Promise<AuthResponse> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(googleData),
      });

      const jsonResponse = await handleApiResponse<AuthResponse>(
        response,
        "Failed to login with Google"
      );

      useAuthStore.getState().setToken(jsonResponse.token);
      useAuthStore.getState().setUser(jsonResponse.user);

      // Store the token
      // TokenStorage.setToken(jsonResponse.token);
      // Cache the user data
      // authApi.updateUserCache(jsonResponse.user);
      return jsonResponse;
    } catch (error) {
      console.error("...>>> Error during Google login:", error);
      throw error;
    }
  },

  // Logout user
  // Login with Apple
  appleLogin: async (appleData: AppleAuthData): Promise<AuthResponse> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/apple`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(appleData),
      });

      const jsonResponse = await handleApiResponse<AuthResponse>(
        response,
        "Failed to login with Apple"
      );

      useAuthStore.getState().setToken(jsonResponse.token);
      useAuthStore.getState().setUser(jsonResponse.user);

      return jsonResponse;
    } catch (error) {
      console.error("...>>> Error during Apple login:", error);
      throw error;
    }
  },

  // Login with password
  passwordLogin: async (
    userEmail: string,
    user_password: string
  ): Promise<AuthResponse> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_email: userEmail,
          user_password: user_password,
        }),
      });

      const jsonResponse = await handleApiResponse<AuthResponse>(
        response,
        "Failed to login"
      );

      useAuthStore.getState().setToken(jsonResponse.token);
      useAuthStore.getState().setUser(jsonResponse.user);

      // Store the token
      // TokenStorage.setToken(jsonResponse.token);
      // authApi.updateUserCache(jsonResponse.user);

      return jsonResponse;
    } catch (error) {
      console.error("Error during password login:", error);
      throw error;
    }
  },
  logout: async (): Promise<{ success: boolean; message: string }> => {
    const logout = useAuthStore.getState().logout;
    logout();

    // Remove token and clear cache first for faster UI response
    // TokenStorage.removeToken();
    if (window.google) {
      window.google.accounts?.id?.disableAutoSelect();
    }

    // Call the server logout endpoint
    const response = await fetch(`${API_BASE_URL}/api/auth/logout`, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.error("...>>> Logout failed on server");
      return { success: true, message: "Logged out successfully" };
    }

    const jsonResponse = await response.json();
    return jsonResponse;
  },
};
